import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { safeHtml } from "@/lib/sanitize";

export default function BlogDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [related, setRelated] = useState([]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true); setNotFound(false); setRelated([]);
            try {
                const { data } = await api.get(`/blog/${slug}`);
                if (cancelled) return;
                setPost(data);
                // Fire related in background; silent fail is fine.
                try {
                    const { data: list } = await api.get("/blog", { params: { category: data.category, limit: 10 } });
                    if (!cancelled) {
                        const others = (list.posts || []).filter((p) => p.slug !== data.slug).slice(0, 3);
                        setRelated(others);
                    }
                } catch (_e) { /* non-fatal */ }
            } catch (err) {
                if (!cancelled) setNotFound(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center bg-background">
                <div className="text-muted-foreground text-sm">Loading article…</div>
            </div>
        );
    }

    if (notFound || !post) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center bg-background text-center p-6" data-testid="blog-not-found">
                <h1 className="font-heading text-3xl text-foreground">Article not found</h1>
                <p className="text-muted-foreground mt-2">It may have moved or been unpublished.</p>
                <button
                    onClick={() => navigate("/resources")}
                    className="mt-6 inline-flex items-center gap-2 text-brand-green hover:underline"
                >
                    <ArrowLeft size={16} /> Back to the blog
                </button>
            </div>
        );
    }

    const seoTitle = post.seo_title || post.title;
    const seoDesc = post.seo_description || post.excerpt;
    const siteOrigin = typeof window !== "undefined" ? window.location.origin : "";
    const canonical = `${siteOrigin}/blog/${post.slug}`;
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.title,
        "description": seoDesc,
        "image": post.cover_image_url || undefined,
        "author": { "@type": "Organization", "name": post.author || "Simonara Talent Solutions" },
        "publisher": {
            "@type": "Organization",
            "name": "Simonara Talent Solutions",
        },
        "datePublished": post.published_at || post.created_at,
        "dateModified": post.updated_at || post.published_at,
        "mainEntityOfPage": canonical,
    };

    return (
        <div className="bg-background" data-testid="blog-detail-page">
            <Helmet>
                <title>{seoTitle} · Simonara Blog</title>
                <meta name="description" content={seoDesc} />
                <link rel="canonical" href={canonical} />
                <meta property="og:title" content={seoTitle} />
                <meta property="og:description" content={seoDesc} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={canonical} />
                {post.cover_image_url ? <meta property="og:image" content={post.cover_image_url} /> : null}
                <meta name="twitter:card" content={post.cover_image_url ? "summary_large_image" : "summary"} />
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            </Helmet>

            {/* Hero */}
            <section className="bg-brand-navy dark:bg-slate-900 text-white pt-16 pb-14">
                <div className="container-page max-w-3xl">
                    <Link
                        to="/resources"
                        data-testid="blog-detail-back"
                        className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white"
                    >
                        <ArrowLeft size={14} /> Back to all articles
                    </Link>
                    <div className="mt-6 flex items-center gap-2 flex-wrap">
                        <Badge className="bg-brand-green/20 text-brand-green border border-brand-green/40">{post.category}</Badge>
                        {(post.tags || []).slice(0, 3).map((t) => (
                            <Badge key={t} variant="outline" className="text-white/80 border-white/20">{t}</Badge>
                        ))}
                    </div>
                    <h1 className="mt-4 font-heading font-medium text-3xl lg:text-5xl leading-tight tracking-tight">
                        {post.title}
                    </h1>
                    {post.excerpt && (
                        <p className="mt-5 text-white/80 text-lg leading-relaxed">{post.excerpt}</p>
                    )}
                    <div className="mt-6 flex items-center gap-5 text-sm text-white/60">
                        {post.reading_time && (
                            <span className="inline-flex items-center gap-1.5"><Clock size={13} /> {post.reading_time}</span>
                        )}
                        {post.published_at && (
                            <span className="inline-flex items-center gap-1.5">
                                <Calendar size={13} /> {new Date(post.published_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                            </span>
                        )}
                    </div>
                </div>
            </section>

            {post.cover_image_url && (
                <div className="container-page max-w-3xl -mt-6">
                    <img
                        src={post.cover_image_url}
                        alt=""
                        aria-hidden="true"
                        className="w-full rounded-xl shadow-lg border border-border object-cover max-h-[460px]"
                    />
                </div>
            )}

            <article className="container-page max-w-3xl py-14">
                <div
                    data-testid="blog-detail-body"
                    className="prose prose-slate dark:prose-invert max-w-none prose-headings:font-heading prose-a:text-brand-green"
                    dangerouslySetInnerHTML={{ __html: safeHtml(post.body) }}
                />
                <div className="mt-14 pt-8 border-t border-border flex items-center justify-between gap-4 flex-wrap">
                    <Link
                        to="/resources"
                        data-testid="blog-detail-back-bottom"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-brand-green hover:underline"
                    >
                        <ArrowLeft size={14} /> More articles
                    </Link>
                    <Link
                        to="/contact"
                        className="btn-outline inline-flex items-center text-sm"
                        data-testid="blog-detail-contact"
                    >
                        Talk to a Simonara strategist
                    </Link>
                </div>
            </article>

            {related.length > 0 && (
                <section
                    className="bg-muted/40 border-t border-border"
                    data-testid="blog-detail-related"
                >
                    <div className="container-page max-w-5xl py-14">
                        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
                            <div>
                                <p className="overline">Keep reading</p>
                                <h2 className="mt-2 font-heading font-medium text-2xl sm:text-3xl text-foreground">
                                    More in {post.category}
                                </h2>
                            </div>
                            <Link
                                to="/resources#blog"
                                className="text-sm font-semibold text-brand-green hover:underline"
                                data-testid="blog-detail-related-all"
                            >
                                All articles →
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            {related.map((r) => (
                                <Link
                                    key={r.slug}
                                    to={`/blog/${r.slug}`}
                                    data-testid={`blog-related-card-${r.slug}`}
                                    className="card-soft p-5 flex flex-col h-full hover:border-emerald-500/40 hover:-translate-y-0.5 transition-all bg-background"
                                >
                                    {r.cover_image_url ? (
                                        <img
                                            src={r.cover_image_url}
                                            alt=""
                                            aria-hidden="true"
                                            className="mb-4 rounded-lg h-32 w-full object-cover border border-border"
                                        />
                                    ) : null}
                                    <Badge variant="outline" className="text-[11px] text-muted-foreground w-max">
                                        {r.category}
                                    </Badge>
                                    <h3 className="mt-3 font-heading font-semibold text-base text-foreground leading-snug">
                                        {r.title}
                                    </h3>
                                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3 flex-1">
                                        {r.excerpt}
                                    </p>
                                    <span className="mt-4 inline-flex items-center gap-1 text-xs text-muted-foreground">
                                        <Clock size={12} /> {r.reading_time || "5 min read"}
                                    </span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
